import { ViewIcon, EditIcon } from "@chakra-ui/icons";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  IconButton,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Text,
  Image,
  Box,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { useState } from "react";
import { ChatState } from "../../Context/ChatProvider";
import AvatarUpload from "../ui/AvatarUpload";
import {
  Ornament,
  MailGlyph,
  PhoneGlyph,
  BrushGlyph,
} from "../ui/Icons";

const avatarFallback = (name) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128"><rect width="128" height="128" fill="#8D414B"/><text x="64" y="80" font-family="Georgia, serif" font-size="52" fill="#FBF0D9" text-anchor="middle">${(
      name || "?"
    )
      .charAt(0)
      .toUpperCase()}</text></svg>`
  )}`;

const Row = ({ icon, label, value }) =>
  value ? (
    <Box d="flex" alignItems="flex-start" w="100%" py={1.5}>
      <Box mt="3px" mr={2.5} minW="15px">
        {icon}
      </Box>
      <Box minW={0}>
        <Text fontSize="11px" color="rgba(36,61,39,0.55)" lineHeight="1.2">
          {label}
        </Text>
        <Text fontSize="14px" color="#243D27" wordBreak="break-word">
          {value}
        </Text>
      </Box>
    </Box>
  ) : null;

const ProfileModal = ({ user, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user: me, setUser } = ChatState();

  const [profile, setProfile] = useState(user);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const isMe = me && user && me._id === user._id;

  // при открытии подтягиваем полный профиль — в списках чатов полей меньше
  const open = async () => {
    setProfile(user);
    setEditing(false);
    onOpen();
    if (!user?._id) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/user/${user._id}`, {
        headers: { Authorization: `Bearer ${me?.token}` },
      });
      setProfile(data);
    } catch (e) {
      /* покажем то, что есть */
    } finally {
      setLoading(false);
    }
  };

  const startEdit = () => {
    setForm({
      name: profile?.name || "",
      fullName: profile?.fullName || "",
      phone: profile?.phone || "",
      craft: profile?.craft || "",
      about: profile?.about || "",
      pic: profile?.pic || "",
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.name?.trim()) {
      toast({
        title: "Имя не может быть пустым",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setSaving(true);
      const { data } = await axios.put("/api/user/profile", form, {
        headers: { Authorization: `Bearer ${me.token}` },
      });
      setProfile(data);
      // обновляем себя в приложении, токен оставляем прежний
      const fresh = { ...me, ...data, token: me.token };
      setUser(fresh);
      localStorage.setItem("userInfo", JSON.stringify(fresh));
      setEditing(false);
      toast({
        title: "Профиль обновлён",
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Не удалось сохранить",
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: form[key] || "",
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const labelStyle = { color: "#243D27", fontSize: "13px", mb: 1 };

  return (
    <>
      {children ? (
        <span onClick={open}>{children}</span>
      ) : (
        <IconButton
          d={{ base: "flex" }}
          icon={<ViewIcon />}
          onClick={open}
          borderRadius="12px"
          bg="#F6E8C6"
          color="#243D27"
          _hover={{ bg: "#EAD9AC" }}
        />
      )}

      <Modal size="md" onClose={onClose} isOpen={isOpen} isCentered scrollBehavior="inside">
        <ModalOverlay bg="rgba(20, 35, 23, 0.45)" />
        <ModalContent maxH="85vh" pb={2}>
          <ModalCloseButton borderRadius="10px" />

          {editing ? (
            <ModalBody d="flex" flexDir="column" pt={10}>
              <Text
                fontFamily="'Prata', serif"
                fontSize="20px"
                color="#243D27"
                textAlign="center"
                mb={1}
              >
                Мой профиль
              </Text>
              <Box d="flex" justifyContent="center" mb={4}>
                <Ornament color="#8D414B" opacity={0.5} width={100} />
              </Box>

              <FormControl mb={3}>
                <FormLabel {...labelStyle}>Фото</FormLabel>
                <AvatarUpload
                  value={form.pic}
                  onChange={(v) => setForm((f) => ({ ...f, pic: v }))}
                />
              </FormControl>

              <FormControl mb={3}>
                <FormLabel {...labelStyle}>Имя в клубе</FormLabel>
                <Input size="sm" borderRadius="10px" {...field("name")} />
              </FormControl>

              <FormControl mb={3}>
                <FormLabel {...labelStyle}>ФИО</FormLabel>
                <Input
                  size="sm"
                  borderRadius="10px"
                  placeholder="Иванова Анна Сергеевна"
                  {...field("fullName")}
                />
              </FormControl>

              <FormControl mb={3}>
                <FormLabel {...labelStyle}>Телефон</FormLabel>
                <Input
                  size="sm"
                  borderRadius="10px"
                  placeholder="+7 900 000-00-00"
                  {...field("phone")}
                />
              </FormControl>

              <FormControl mb={3}>
                <FormLabel {...labelStyle}>Чем занимаюсь</FormLabel>
                <Input
                  size="sm"
                  borderRadius="10px"
                  placeholder="акварель, портрет, керамика…"
                  {...field("craft")}
                />
              </FormControl>

              <FormControl mb={2}>
                <FormLabel {...labelStyle}>Пара слов о себе</FormLabel>
                <Textarea
                  size="sm"
                  borderRadius="10px"
                  rows={3}
                  maxLength={300}
                  bg="#FDF7E9"
                  borderColor="#D3BC8D"
                  _hover={{ borderColor: "#B89F6E" }}
                  _focus={{
                    borderColor: "#8D414B",
                    boxShadow: "0 0 0 3px rgba(141, 65, 75, 0.18)",
                  }}
                  placeholder="Пишу маслом с 2015 года, люблю пленэры и северный свет"
                  {...field("about")}
                />
                <Text fontSize="11px" color="rgba(36,61,39,0.5)" textAlign="right">
                  {(form.about || "").length}/300
                </Text>
              </FormControl>

              <Box d="flex" mt={2}>
                <Button
                  flex="1"
                  colorScheme="burgundy"
                  isLoading={saving}
                  onClick={save}
                  _hover={{ bg: "#7A3841" }}
                >
                  Сохранить
                </Button>
                <Button
                  ml={2}
                  variant="ghost"
                  colorScheme="pine"
                  onClick={() => setEditing(false)}
                >
                  Отмена
                </Button>
              </Box>
            </ModalBody>
          ) : (
            <>
              <ModalBody d="flex" flexDir="column" alignItems="center" pt={10}>
                <Image
                  borderRadius="full"
                  boxSize="120px"
                  objectFit="cover"
                  border="3px solid #8D414B"
                  boxShadow="0 12px 30px -12px rgba(141, 65, 75, 0.6)"
                  src={profile?.pic || avatarFallback(profile?.name)}
                  fallbackSrc={avatarFallback(profile?.name)}
                  alt={profile?.name}
                />
                <Text
                  fontSize="24px"
                  fontFamily="'Prata', serif"
                  color="#243D27"
                  mt={4}
                  textAlign="center"
                >
                  {profile?.name}
                </Text>
                {profile?.fullName && (
                  <Text fontSize="13px" color="rgba(36,61,39,0.6)" mt={0.5}>
                    {profile.fullName}
                  </Text>
                )}
                <Box mt={3} mb={1}>
                  <Ornament color="#8D414B" opacity={0.5} width={100} />
                </Box>

                {loading && <Spinner size="sm" color="#8D414B" my={2} />}

                <Box
                  w="100%"
                  bg="#F6E8C6"
                  border="1px solid #E4D2A6"
                  borderRadius="14px"
                  px={4}
                  py={2}
                  mt={2}
                >
                  <Row
                    icon={<MailGlyph />}
                    label="Почта"
                    value={profile?.email}
                  />
                  <Row
                    icon={<PhoneGlyph />}
                    label="Телефон"
                    value={profile?.phone}
                  />
                  <Row
                    icon={<BrushGlyph />}
                    label="Чем занимаюсь"
                    value={profile?.craft}
                  />
                </Box>

                {profile?.about && (
                  <Text
                    fontSize="14px"
                    color="#243D27"
                    mt={3}
                    lineHeight="1.55"
                    textAlign="center"
                    wordBreak="break-word"
                  >
                    {profile.about}
                  </Text>
                )}

                {isMe && !profile?.phone && !profile?.craft && !profile?.about && (
                  <Text
                    fontSize="12px"
                    color="rgba(36,61,39,0.55)"
                    mt={3}
                    textAlign="center"
                  >
                    Расскажите о себе — художникам клуба будет проще вас узнать
                  </Text>
                )}
              </ModalBody>

              <ModalFooter justifyContent="center">
                {isMe && (
                  <Button
                    leftIcon={<EditIcon />}
                    colorScheme="burgundy"
                    px={6}
                    mr={2}
                    onClick={startEdit}
                    _hover={{ bg: "#7A3841" }}
                  >
                    Редактировать
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  variant="outline"
                  colorScheme="burgundy"
                  px={6}
                  _hover={{ bg: "#8D414B", color: "#FBF0D9" }}
                >
                  Закрыть
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProfileModal;
